import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchPropBotI18nContent,
  fetchPropBotAvailableLanguages,
  savePropBotI18nContent,
  getPropBotLanguageStatus,
  PropBotI18nContent
} from '@/services/prop-bot/prop-bot-i18n-service';
import { toast } from 'sonner';

export const usePropBotI18nContent = (
  botId: string | undefined, 
  languageCode: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['propBotI18n', botId, languageCode],
    queryFn: () => fetchPropBotI18nContent(botId!, languageCode),
    enabled: !!botId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const usePropBotAvailableLanguages = (botId: string | undefined) => {
  return useQuery({
    queryKey: ['propBotLanguages', botId],
    queryFn: () => fetchPropBotAvailableLanguages(botId!),
    enabled: !!botId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const usePropBotLanguageStatus = (botId: string | undefined) => {
  return useQuery({
    queryKey: ['propBotLanguageStatus', botId],
    queryFn: () => getPropBotLanguageStatus(botId!),
    enabled: !!botId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSavePropBotI18nContent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (content: PropBotI18nContent) => savePropBotI18nContent(content),
    onSuccess: (_, variables) => {
      toast.success('Translation saved successfully');
      queryClient.invalidateQueries({ 
        queryKey: ['propBotI18n', variables.bot_id, variables.language_code] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['propBotLanguages', variables.bot_id] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['propBotLanguageStatus', variables.bot_id] 
      });
    },
    onError: (error: any) => {
      toast.error(`Error saving translation: ${error.message}`);
    }
  });
};